// import React from 'react';
// import './Header.css';

// const Header = () => {
//   return (
//     <header className="header">
//       <div className="header-content">
//         <h1 className="header-title">Sterling</h1>
//         <div className="header-nav">
//           {/* Future navigation items */}
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;

// src/components/Header.js - Updated with user menu and logout
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BookOpen, User, LogOut, Settings, ChevronDown, X, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import './Header.css';
import { API_BASE_URL } from '../utils/api';

const SettingsModal = ({ open, onClose, qbConnected, qbStatus, onQBStatusUpdate }) => {
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { token } = useAuth();
  
  // Ref to track if we're currently checking QB connection
  const isCheckingQB = useRef(false);

  // Check QuickBooks connection status
  const checkQBConnection = useCallback(async (retries = 3) => {
    if (!token) return;
    
    // Prevent multiple simultaneous calls
    if (isCheckingQB.current) {
      console.log('Header QB connection check already in progress, skipping...');
      return;
    }
    
    isCheckingQB.current = true;
    setCheckingStatus(true);
    
    try {
      // Call the parent's QB status update function
      if (onQBStatusUpdate) {
        await onQBStatusUpdate(retries);
      }
    } finally {
      setCheckingStatus(false);
      isCheckingQB.current = false;
    }
  }, [token, onQBStatusUpdate]);

  // Handle QuickBooks connection
  const handleQBConnect = async () => {
    if (!token) return;
    
    setConnecting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/quickbooks/connect`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthUrl(data.auth_url);
        setShowAuthModal(true);
      } else {
        throw new Error('Failed to initiate QuickBooks connection');
      }
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error);
      alert('Error connecting to QuickBooks. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Handle QuickBooks disconnection
  const handleQBDisconnect = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/quickbooks/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // The parent component will handle updating the QB status
        // by calling onQBStatusUpdate after the disconnect
        if (onQBStatusUpdate) {
          setTimeout(() => {
            onQBStatusUpdate();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error disconnecting from QuickBooks:', error);
    }
  };

  // Handle auth completion
  const handleAuthComplete = () => {
    setShowAuthModal(false);
    setTimeout(() => {
      checkQBConnection();
    }, 2000);
  };

  // Only check status if token is present
  useEffect(() => {
    if (token) {
      checkQBConnection();
    }
  }, [token, checkQBConnection]);

  // Handle click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="settings-modal-overlay" onClick={handleOverlayClick}>
        <div className="settings-modal">
          <button className="settings-modal-close" onClick={onClose} title="Close">
            <X size={20} />
          </button>
          <h2>Settings</h2>
          <div className="settings-content">
            <div className="settings-section">
              <h3>QuickBooks Connection</h3>
              <div className="qb-status-section">
                {checkingStatus ? (
                  <div className="qb-status checking">
                    <Loader2 size={16} className="spinning" />
                    <span>Checking connection...</span>
                  </div>
                ) : qbConnected ? (
                  <div className="qb-status connected">
                    <CheckCircle size={16} />
                    <span>Connected to QuickBooks</span>
                    {qbStatus?.company_name && (
                      <span className="company-name">({qbStatus.company_name})</span>
                    )}
                    <button 
                      className="qb-disconnect-btn"
                      onClick={handleQBDisconnect}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : qbStatus && qbStatus.error === 'refresh_failed' ? (
                  <div className="qb-status disconnected">
                    <AlertCircle size={16} />
                    <span>QuickBooks connection expired</span>
                    <div className="qb-action-buttons">
                      <button 
                        className="qb-connect-btn"
                        onClick={handleQBConnect}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <>
                            <Loader2 size={16} className="spinning" />
                            Reconnecting...
                          </>
                        ) : (
                          <>
                            <BookOpen size={16} />
                            Reconnect QuickBooks
                          </>
                        )}
                      </button>
                      <button 
                        className="qb-retry-btn"
                        onClick={() => checkQBConnection()}
                        disabled={checkingStatus}
                        title="Retry status check"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                  </div>
                ) : qbStatus && qbStatus.error ? (
                  <div className="qb-status error">
                    <AlertCircle size={16} />
                    <span>Connection Error: {qbStatus.error}</span>
                    <div className="qb-action-buttons">
                      <button 
                        className="qb-connect-btn"
                        onClick={handleQBConnect}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <>
                            <Loader2 size={16} className="spinning" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <BookOpen size={16} />
                            Connect QuickBooks
                          </>
                        )}
                      </button>
                      <button 
                        className="qb-retry-btn"
                        onClick={() => checkQBConnection()}
                        disabled={checkingStatus}
                        title="Retry status check"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="qb-status disconnected">
                    <AlertCircle size={16} />
                    <span>Not connected to QuickBooks</span>
                    <div className="qb-action-buttons">
                      <button 
                        className="qb-connect-btn"
                        onClick={handleQBConnect}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <>
                            <Loader2 size={16} className="spinning" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <BookOpen size={16} />
                            Connect QuickBooks
                          </>
                        )}
                      </button>
                      <button 
                        className="qb-retry-btn"
                        onClick={() => checkQBConnection()}
                        disabled={checkingStatus}
                        title="Retry status check"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAuthModal && (
        <AuthModal 
          authUrl={authUrl}
          onClose={() => setShowAuthModal(false)}
          onComplete={handleAuthComplete}
        />
      )}
    </>
  );
};

const AuthModal = ({ authUrl, onClose, onComplete }) => {
  const [authCompleted, setAuthCompleted] = useState(false);

  const handleAuthWindow = () => {
    const authWindow = window.open(
      authUrl, 
      'quickbooks-auth', 
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    // Poll for window closure
    const checkClosed = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkClosed);
        setAuthCompleted(true);
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    }, 1000);
  };

  // Handle click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="auth-modal-overlay" onClick={handleOverlayClick}>
      <div className="auth-modal">
        <div className="auth-modal-content">
          {!authCompleted ? (
            <>
              <h3>Connect to QuickBooks</h3>
              <p>Click the button below to authorize Sterling to access your QuickBooks data.</p>
              
              <button 
                className="auth-btn"
                onClick={handleAuthWindow}
              >
                <BookOpen size={16} />
                Authorize QuickBooks
              </button>
              
              <button 
                className="auth-cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <CheckCircle size={48} className="auth-success-icon" />
              <h3>Authorization Complete!</h3>
              <p>Your QuickBooks account has been successfully connected.</p>
              <button 
                className="auth-done-btn"
                onClick={onComplete}
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Header = ({ onGoToHome, showSettings, onCloseSettings, onOpenSettings, qbConnected, qbStatus, onQBStatusUpdate }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const isDemoMode = user?.email === 'demo@sterling.com' || localStorage.getItem('demo_mode') === 'true';

  return (
    <header className="app-header">
      <div className="header-content">
        {/* Logo */}
        <div className="header-logo" onClick={onGoToHome} style={{ cursor: 'pointer' }}>
          <BookOpen size={24} />
          <span className="logo-text">Sterling</span>
        </div>

        {/* User Menu */}
        {isAuthenticated && (
          <div className="user-menu-container" ref={menuRef}>
            <button 
              className="user-menu-trigger"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-info">
                <div className="user-avatar">
                  <User size={16} />
                </div>
                <div className="user-details">
                  <span className="user-name">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className="user-company">
                    {user?.company_name} {isDemoMode && '(Demo)'}
                  </span>
                </div>
              </div>
              <ChevronDown size={16} className={`chevron ${showUserMenu ? 'rotated' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="user-menu-dropdown">
                <div className="user-menu-header">
                  <div className="user-menu-avatar">
                    <User size={20} />
                  </div>
                  <div className="user-menu-info">
                    <div className="user-menu-name">
                      {user?.first_name} {user?.last_name}
                    </div>
                    <div className="user-menu-email">
                      {user?.email}
                    </div>
                    <div className="user-menu-company">
                      {user?.company_name}
                    </div>
                  </div>
                </div>

                <div className="user-menu-divider"></div>

                <div className="user-menu-items">
                  <button className="user-menu-item" onClick={() => { onOpenSettings(); setShowUserMenu(false); }}>
                    <Settings size={16} />
                    <span>Settings</span>
                  </button>
                  <button className="user-menu-item logout" onClick={handleLogout}>
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <SettingsModal 
        open={showSettings} 
        onClose={onCloseSettings} 
        qbConnected={qbConnected}
        qbStatus={qbStatus}
        onQBStatusUpdate={onQBStatusUpdate}
      />
    </header>
  );
};

export default Header;